import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  Container,
  Stack,
  Typography,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  TextField,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
} from "@mui/material";

import { FormInput } from "components/parts/form";

import axios from "axios";

import { IStudentDetailSchema, studentSchema } from "./parts/hooks/schemas";
import { IStudentDetail, clearData } from "./parts";

import {
  useGetStudent,
  useAddStudent,
  useUpdateStudent,
  useDeleteStudent,
} from "api/management/students";

import { useGetGrades } from "api/management/grades";
import { useGetSections } from "api/management/sections";

import { IGrade } from "components/pages/management/gradeSections/hooks/grades/parts";
import { ISection } from "components/pages/management/gradeSections/hooks/sections/parts";

import {
  ProgressBackdropDialog,
  ConfirmationDialog,
  MessageDialog,
} from "components/parts/dialogs";
import { useI18n } from "i18n";

const Page = (props: any) => {
  const { t } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const { id, mode } = location["state"]
    ? location["state"]
    : { id: null, mode: "add" };

  const editMode = mode && (mode === "edit" || mode === "add") ? mode : "add";

  const { loading: getStudentLoading, service: getStudent } = useGetStudent();
  const { loading: getGradesLoading, service: getGrades } = useGetGrades();
  const { loading: getSectionsLoading, service: getSections } =
    useGetSections();
  const { loading: addLoading, service: addStudent } = useAddStudent();
  const { loading: updateLoading, service: updateStudent } = useUpdateStudent();
  const { loading: deleteLoading, service: deleteStudent } = useDeleteStudent();

  const [student, setStudent] = useState<IStudentDetail>(null);
  const [grades, setGrades] = useState<IGrade[]>([]);
  const [sections, setSections] = useState<ISection[]>([]);

  const [formData, setFormData] = useState<IStudentDetailSchema>();

  const methods = useForm<IStudentDetailSchema>({
    resolver: yupResolver(studentSchema),
    defaultValues: clearData,
  });

  const { handleSubmit: formSubmit, reset } = methods;

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openNoticeDialog, setOpenNoticeDialog] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);

  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [noticeMessage, setNoticeMessage] = useState<string>("");

  const onSubmit = (data: IStudentDetailSchema) => {
    const formattedData = {
      ...data,
    };

    setFormData(formattedData);
    setConfirmMessage(
      editMode === "edit"
        ? t("common.doUpdate")
        : t("common.doRegister")
    );
    setOpenConfirmDialog(true);
  };

  const handleDeleteClick = () => {
    setOpenDeleteConfirmDialog(true);
  };

  useEffect(() => {
    getGrades()
      .then((response) => {
        const { data: result } = response;
        const { data } = result;
        setGrades(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.log(error);
      });

    getSections()
      .then((response) => {
        const { data: result } = response;
        const { data } = result;
        setSections(data);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.log(error);
      });

    if (editMode === "edit" && id) {
      getStudent(id)
        .then((response) => {
          const { data: result } = response;
          return result;
        })
        .then((result) => {
          const { data } = result;
          setStudent(data);
          reset(data);
        })
        .catch((error) => {
          if (axios.isCancel(error)) return;
          console.log(error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center">
          <Typography
            variant="h1"
            gutterBottom
            sx={{ fontSize: "24px", fontWeight: "bold", mb: 4 }}
          >
            {editMode === "edit" ? t("students.editTitle") : t("students.addTitle")}
          </Typography>
        </Stack>
        <Box sx={{ width: "100%" }}>
          <FormProvider {...methods}>
            <form onSubmit={formSubmit(onSubmit)}>
              <Stack direction="row" alignItems="center">
                <Typography
                  variant="h2"
                  gutterBottom
                  sx={{ fontSize: "20px", mb: 2 }}
                >
                  {t("students.information")}
                </Typography>
              </Stack>
              <Stack direction="column" spacing={2}>
                <Stack direction={{ sm: "column", md: "row" }} spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <FormControl required>
                      <FormLabel component="legend">{t("field.firstName")}</FormLabel>
                      <FormInput
                        name="firstName"
                        render={(field, fieldState) => {
                          return (
                            <TextField
                              {...field}
                              type={"text"}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          );
                        }}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel component="legend">{t("field.lastName")}</FormLabel>
                      <FormInput
                        name="lastName"
                        render={(field, fieldState) => {
                          return (
                            <TextField
                              {...field}
                              type={"text"}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                            />
                          );
                        }}
                      />
                    </FormControl>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <FormControl
                      component="fieldset"
                      error={!!methods.formState.errors.gender}
                      required
                    >
                      <FormLabel component="legend">{t("field.gender")}</FormLabel>
                      <FormInput
                        name="gender"
                        render={(field, fieldState) => {
                          return (
                            <RadioGroup {...field} row>
                              <FormControlLabel
                                value="MALE"
                                control={<Radio />}
                                label={t("field.male")}
                              />
                              <FormControlLabel
                                value="FEMALE"
                                control={<Radio />}
                                label={t("field.female")}
                              />
                            </RadioGroup>
                          );
                        }}
                      />
                      <FormHelperText>
                        {methods.formState.errors.gender?.message}
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </Stack>

                <Stack direction="row" alignItems="center">
                  <Typography
                    variant="h2"
                    gutterBottom
                    sx={{ fontSize: "20px", mb: 2 }}
                  >
                    {t("students.gradeSectionInformation")}
                  </Typography>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl
                    component="fieldset"
                    error={!!methods.formState.errors.gradeId}
                    required
                  >
                    <FormLabel component="legend">{t("field.grade")}</FormLabel>
                    <FormInput
                      name="gradeId"
                      render={(field, fieldState) => {
                        return (
                          <Select {...field}>
                            <MenuItem value={-1} disabled>
                              {t("students.selectGrade")}
                            </MenuItem>
                            {grades.map((item, index) => (
                              <MenuItem key={index} value={item["id"]}>
                                {item["name"]}
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />

                    <FormHelperText>
                      {methods.formState.errors.gradeId?.message}
                    </FormHelperText>
                  </FormControl>
                  <FormControl
                    component="fieldset"
                    error={!!methods.formState.errors.sectionId}
                    required
                  >
                    <FormLabel component="legend">{t("field.section")}</FormLabel>
                    <FormInput
                      name="sectionId"
                      render={(field, fieldState) => {
                        return (
                          <Select {...field}>
                            <MenuItem value={-1} disabled>
                              {t("students.selectSection")}
                            </MenuItem>
                            {sections.map((item, index) => (
                              <MenuItem key={index} value={item["id"]}>
                                {item["name"]}
                              </MenuItem>
                            ))}
                          </Select>
                        );
                      }}
                    />
                    <FormHelperText>
                      {methods.formState.errors.sectionId?.message}
                    </FormHelperText>
                  </FormControl>
                </Stack>
              </Stack>

              <Button type="submit" color="primary">
                {editMode === "edit" ? t("common.update") : t("common.register")}
              </Button>
              {student && (
                <Button color="warning" onClick={handleDeleteClick}>
                  {t("common.delete")}
                </Button>
              )}
            </form>
          </FormProvider>
        </Box>
      </Container>
      <ConfirmationDialog
        open={openConfirmDialog}
        title={t("common.confirmation")}
        message={confirmMessage}
        handleSubmit={async () => {
          try {
            setOpenConfirmDialog(false);
            console.log(formData);
            if (editMode === "edit") {
              await updateStudent(id, formData);
            } else {
              await addStudent(formData);
            }
            setNoticeMessage(
              editMode === "edit"
                ? t("common.successUpdated")
                : t("common.successRegistered")
            );
            setOpenNoticeDialog(true);
          } catch (error) {}
        }}
        negativeText={t("common.cancel")}
        positiveText={t("common.ok")}
        handleClose={() => setOpenConfirmDialog(false)}
      />

      <ConfirmationDialog
        open={openDeleteConfirmDialog}
        title={t("common.warning")}
        message={t("common.doDeleteWarning")}
        handleSubmit={async () => {
          try {
            if (editMode === "edit") {
              await deleteStudent(id);
            }
            setOpenDeleteConfirmDialog(false);
            setNoticeMessage(t("common.successDeleted"));
            setOpenNoticeDialog(true);
          } catch (error) {}
        }}
        negativeText={t("common.cancel")}
        positiveText={t("common.ok")}
        handleClose={() => setOpenDeleteConfirmDialog(false)}
      />

      <MessageDialog
        open={openNoticeDialog}
        title={t("common.notice")}
        message={noticeMessage}
        handleClose={() => {
          setOpenNoticeDialog(false);
          nav("/management/students");
        }}
      />
      <ProgressBackdropDialog
        open={
          getStudentLoading ||
          getGradesLoading ||
          getSectionsLoading ||
          addLoading ||
          updateLoading ||
          deleteLoading
        }
      />
    </>
  );
};

export default Page;
